/**
 * HVP Main Entry Point
 *
 * Initializes the Haru Variations Pro controller
 * This is the entry point for webpack build
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

import { HVPCascadeController } from './cascade-controller.js';
import { initNoMatchNotice } from './no-match-notice.js';

// Make available globally for debugging
window.HVPCascadeController = HVPCascadeController;

// Attach the no-match notice listener as early as possible. It is a passive
// DOM-event consumer (hvp_no_match / hvp_variation_found / hvp_reset /
// hvp_selection_changed) and does not depend on the controller being ready,
// so wiring it up before init guarantees no early no-match is missed.
initNoMatchNotice();

/**
 * Initialize HVP Controller
 */
function initializeHVP() {
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] HARU VARIATIONS PRO - ENTRY POINT');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] ========================================');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Script: main.js (ES6 Module Entry)');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Target Controller: HVPCascadeController (Smart Cascade)');

    // Check if config is available
    if (!window.hvpData || !window.hvpData.productId) {
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] WARNING: hvpData configuration not found');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Possible reasons:');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   - Not on a product page');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   - Product does not have enough variations');
        window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   - HVP plugin not properly enqueued');
        window.hvpData && window.hvpData.debug && console.log('[HVP] Configuration not found');
        return;
    }

    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG] Configuration found:');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Product ID:', window.hvpData.productId);
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Variation Count:', window.hvpData.variationCount);
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Min Variations:', window.hvpData.minVariations);
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Cascade Mode:', window.hvpData.cascadeMode || 'default');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Smart Cascade:', window.hvpData.smartCascadeEnabled !== false ? 'enabled' : 'disabled');
    window.hvpData && window.hvpData.debug && console.log('[HVP DEBUG]   Debug Mode:', window.hvpData.debug);

    window.hvpData && window.hvpData.debug && console.log(
        '%c[HVP] Haru Variations Pro Loading...',
        'background: #6366f1; color: #fff; padding: 4px 8px; border-radius: 4px; font-weight: bold;'
    );

    // Try to find variations form
    function tryInitialize(attempt = 1) {
        const MAX_ATTEMPTS = 10;
        const RETRY_DELAY = 150;

        const form = document.querySelector('.variations_form');

        if (form) {
            window.hvpData && window.hvpData.debug && console.log(`[HVP] Form found on attempt ${attempt}`);

            // Create controller instance
            const controller = new HVPCascadeController(window.hvpData);

            // Initialize
            controller.init().then(success => {
                if (success) {
                    // Store globally
                    window.hvpController = controller;

                    window.hvpData && window.hvpData.debug && console.log(
                        '%c[HVP] Controller READY ✓',
                        'background: #22c55e; color: #000; padding: 4px 8px; border-radius: 4px; font-weight: bold;',
                        `\nProduct ID: ${controller.productId}`,
                        `\nVariations: ${controller.variationCount}`,
                        `\nAttributes: ${controller.attributeOrder.length}`
                    );

                    // Fire ready event
                    document.dispatchEvent(new CustomEvent('hvp_ready', {
                        detail: { controller }
                    }));

                    // Back-compat alias: the theme cascade lock, rps-smart-hide
                    // (one-shot init fallback) and rps-coachmarks all listen for
                    // this name; it was never dispatched before.
                    controller.fireCustomEvent('hvp:cascade:ready', { controller });

                } else {
                    window.hvpData && window.hvpData.debug && console.log(
                        '%c[HVP] Controller INACTIVE',
                        'background: #f59e0b; color: #000; padding: 4px 8px; border-radius: 4px;',
                        `\nVariations: ${window.hvpData.variationCount}`,
                        `< Threshold: ${window.hvpData.minVariations}`
                    );
                }
            }).catch(error => {
                console.error('[HVP] Initialization failed:', error);
            });

            return;
        }

        // Retry
        if (attempt < MAX_ATTEMPTS) {
            window.hvpData && window.hvpData.debug && console.log(`[HVP] Form not found, retry ${attempt}/${MAX_ATTEMPTS}...`);
            setTimeout(() => tryInitialize(attempt + 1), RETRY_DELAY);

        } else {
            window.hvpData && window.hvpData.debug && console.log('[HVP] Form not found after retries, using MutationObserver...');

            // Use MutationObserver as fallback
            const observer = new MutationObserver((mutations, obs) => {
                const form = document.querySelector('.variations_form');

                if (form) {
                    obs.disconnect();
                    window.hvpData && window.hvpData.debug && console.log('[HVP] Form detected via MutationObserver');
                    tryInitialize(1);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true
            });

            // Disconnect after 10 seconds
            setTimeout(() => {
                observer.disconnect();
                window.hvpData && window.hvpData.debug && console.log('[HVP] MutationObserver stopped');
            }, 10000);
        }
    }

    // Start initialization immediately at script execution. The form renders
    // above this script's print point (footer), so waiting for
    // DOMContentLoaded only serialized the boot behind every other deferred
    // script on the page (~90 on cached product pages — multi-hundred-ms to
    // seconds of dead, unhandled dropdowns). The retry loop + MutationObserver
    // in tryInitialize already cover any context where the form arrives later
    // (AJAX-injected designer panels).
    tryInitialize(1);
}

// Initialize
initializeHVP();

// Export for module usage
export { HVPCascadeController };
export default HVPCascadeController;
